import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { QuipliLink } from '../../../components/link/quipli-link';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { SelectForm } from '../../../components/select-form/select-form';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { SearchDropdownOption } from '../../../components/dropdown/search-dropdown';
import { ApiError } from '../../../clients/axios-instance';
import { Badge } from '../../../components/badge/badge';
import {
  OrderReview,
  OrderReviewSearchPaginationRequest,
  ReviewFeedback,
  ReviewStatus,
} from '../../../clients/quipli-service/customer-reviews/order-reviews';
import { useYorentClient } from '../../../clients/yorent/yorent-client';
import { toast } from '../../../util/toasts';
import { Tooltip } from '../../../components/tooltip/tooltip';
import './reviews.scss';
import classNames from 'classnames';
import { isMobileOnly } from 'react-device-detect';
import { PieChartSimple } from '../../../components/charts/pie-chart';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';

export const CustomerReviews = () => {
  const { searchCustomerReviews, getCustomerReviewStatistics } = useAPI(),
    { resendCustomerReviewEmail } = useYorentClient();

  const [totalReviews, setTotalReviews] = useState<number>(0),
    [reviewsGroups, setReviewsGroups] = useState<{ label: string; size: number; color: string }[]>([]),
    [statisticsLoading, setStatisticsLoading] = useState<boolean>(true);

  function getStatistics() {
    Promise.all([getCustomerReviewStatistics()]).then(([reviewsStatistics]) => {
      setTotalReviews(reviewsStatistics.totalReviews);
      setReviewsGroups([
        {
          label: 'Negative',
          size: reviewsStatistics.totalDisliked,
          color: 'error-500',
        },
        {
          label: 'Positive',
          size: reviewsStatistics.totalLiked,
          color: 'success-500',
        },
        {
          label: 'Not reviewed',
          size: reviewsStatistics.totalPending,
          color: 'grey-300',
        },
      ]);
      setStatisticsLoading(false);
    });
  }

  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [tablePagination, setTablePagination] = useState<PaginationData>();

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<OrderReviewSearchPaginationRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      statusFilter: params.has('statusFilter') ? (params.get('statusFilter')! as ReviewStatus) : undefined,
      reviewedAfter: params.has('reviewedAfter') ? DateTime.fromISO(params.get('reviewedAfter')!) : undefined,
      reviewedBefore: params.has('reviewedBefore') ? DateTime.fromISO(params.get('reviewedBefore')!) : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setIsTableDataLoading(true);
      searchCustomerReviews(_request)
        .then(({ items, pagination }) => {
          setTablePagination(pagination);
          setTableData(items.map(buildDataRow));
          setIsTableDataLoading(false);
        })
        .catch(() => {
          setIsTableDataLoading(false);
          setTableData([]);
        });
    },
  );

  const ReviewStatusValues: SearchDropdownOption[] = [
    { label: 'Pending', value: ReviewStatus.Pending },
    { label: 'Completed', value: ReviewStatus.Completed },
  ];

  const columns: DataTableColumn[] = [
    { key: 'num', label: '', showRule: 'OnlyDesktop' },
    { key: 'invoice', label: 'Inv No.', showRule: 'OnlyDesktop' },
    { key: 'reviewedBy', label: 'Reviewed by' },
    { key: 'email', label: 'Email' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'created', label: 'Created' },
    { key: 'reviewed', label: 'Reviewed' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '', fixedAsLast: true, showRule: 'OnlyDesktop' },
    { key: 'actionsMobile', label: '', fixedAsLast: true, showRule: 'OnlyMobile' },
  ];

  const buildDataRow = (review: OrderReview, index: number) => {
    return {
      mobileHeader: (
        <Typography type="text-sm" weight="regular">
          <QuipliLink
            to={`orders/view/${review.order.id}`}
            className="underline underline-offset-1 text-[var(--quipli-black-800)]"
          >
            {review.order.orderNumber}
          </QuipliLink>
        </Typography>
      ),
      fields: {
        num: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {index + 1}
          </Typography>
        ),
        invoice: (
          <Typography type="text-sm" weight="regular">
            <QuipliLink
              to={`orders/view/${review.order.id}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {review.order.orderNumber}
            </QuipliLink>
          </Typography>
        ),
        reviewedBy: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {review.user.name}
          </Typography>
        ),
        email: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {review.user.email}
          </Typography>
        ),
        feedback: (
          <Typography
            type="text-sm"
            className={
              review.rating
                ? review.rating === ReviewFeedback.Positive
                  ? 'text-success-600'
                  : 'text-error-500'
                : 'text-grey-400'
            }
          >
            {review.rating ?? '-NA-'}
          </Typography>
        ),
        created: (
          <div>
            <Typography type="text-sm" className="text-quipli-black-800">
              {review.createdAt.toFormat('LLL-dd')}
            </Typography>
            <Typography type="text-sm" className="text-grey-500">
              {review.createdAt.toFormat('hh:mm a').toLowerCase()}
            </Typography>
          </div>
        ),
        reviewed: (
          <div>
            {review.reviewedOn ? (
              <div>
                <Typography type="text-sm" className="text-quipli-black-800">
                  {review.reviewedOn.toFormat('LLL-dd')}
                </Typography>
                <Typography type="text-sm" className="text-grey-500">
                  {review.reviewedOn.toFormat('hh:mm a').toLowerCase()}
                </Typography>
              </div>
            ) : (
              <Typography type="text-sm" className={'text-grey-400'}>
                {'-NA-'}
              </Typography>
            )}
          </div>
        ),
        status: <Badge text={review.status} type={review.status === ReviewStatus.Completed ? 'success' : 'default'} />,
        actions: <Actions review={review} resendEmail={resendEmail} />,
        actionsMobile: <MobileActions review={review} resendEmail={resendEmail} />,
      },
    };
  };

  const resendEmail = async (orderReviewId: number) => {
    resendCustomerReviewEmail(orderReviewId)
      .then((response) => {
        toast(response);
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'something wrong sending email';
        toast(errMsg, 'error');
      });
  };

  useEffect(() => {
    getStatistics();
  }, []);

  return (
    <div className="customer-reviews-list">
      <div className="container-v2 relative p-2">
        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            Customer Reviews
          </Typography>
        </div>

        <div className="pt-10 pb-5 flex items-center justify-between">
          <SearchBar
            data-cy="search-renters"
            placeholder="Search reviews"
            defaultValue={request.keyword}
            setRequest={setRequest}
          />
        </div>

        <div className="flex flex-wrap md:max-lg:grid md:max-lg:grid-cols-2 sm:max-md:grid sm:max-md:grid-cols-2 w-full items-center justify-start gap-2 mb-3 filters-wrapper">
          <div className="w-full md:w-auto">
            <SelectForm
              options={ReviewStatusValues}
              value={
                request.statusFilter
                  ? ReviewStatusValues.find((option) => option.value === request.statusFilter)
                  : undefined
              }
              onChange={(ps) => {
                setRequest((prev) => ({ ...prev, statusFilter: ps?.value as ReviewStatus }));
              }}
              isClearable={true}
              placeholder="Status"
              isSearchable={false}
            />
          </div>
          <div className="w-full md:w-auto requested-dates">
            <DatePicker
              value={request.reviewedAfter}
              onChange={(reviewedAfter) => setRequest((prev) => ({ ...prev, reviewedAfter }))}
              placeholder="Reviewed After"
              clearable
            />
          </div>
          <div className="w-full md:w-auto requested-dates">
            <DatePicker
              value={request.reviewedBefore}
              onChange={(reviewedBefore) => setRequest((prev) => ({ ...prev, reviewedBefore }))}
              placeholder="Reviewed Before"
              clearable
            />
          </div>
        </div>

        <DataTable
          isLoading={isTableDataLoading}
          columnNames={columns}
          data={tableData}
          pagination={tablePagination}
          request={request}
          setRequest={setRequest}
          cypressPrevious="previous_renters_page"
          cypressNext="next_renters_page"
        />
        {!statisticsLoading && totalReviews > 0 && (
          <div className="graphSection mt-5">
            {isMobileOnly ? (
              <div className="flex flex-row justify-between">
                <PieChartSimple data={reviewsGroups}></PieChartSimple>
                <div className="flex flex-col p-2">
                  <div className="flex flex-row space-x-1 mb-2">
                    <Typography type="text-md" weight="regular" className="text-grey-400">
                      Total reviews
                    </Typography>
                    <Typography type="text-md" weight="medium" className="mr-0.5">
                      {totalReviews}
                    </Typography>
                  </div>
                  {reviewsGroups.map((item, index) => (
                    <Legend index={index} color={item.color} label={item.label} size={item.size} />
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex w-full flex-row justify-between">
                  <Typography type="text-md" weight="medium">
                    Customer review statistics
                  </Typography>
                  <div className="flex flex-row space-x-1">
                    <Typography type="text-md" weight="regular" className="text-grey-400">
                      Total reviews
                    </Typography>
                    <Typography type="text-md" weight="medium" className="mr-0.5">
                      {totalReviews}
                    </Typography>
                  </div>
                </div>
                <div className="pt-4 pb-6">
                  <div className="flex flex-row w-full space-x-1">
                    {reviewsGroups.map((item) => (
                      <div
                        className={classNames(
                          'h-full px-4 py-2 rounded block transition-all duration-500',
                          `bg-${item.color}`,
                        )}
                        style={{ width: `${(item.size * 100) / totalReviews}%` }}
                      ></div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-row space-x-5">
                  {reviewsGroups.map((item, index) => (
                    <Legend index={index} color={item.color} label={item.label} size={item.size} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Actions = ({ review, resendEmail }: { review: OrderReview; resendEmail: (orderReviewId: number) => void }) => {
  return (
    <div>
      {review.status === ReviewStatus.Pending ? (
        <Tooltip tooltip="Resend email" type="black">
          <Button
            className="button-tertiary"
            onClick={() => {
              resendEmail(review.order.id);
            }}
            data-cy="resend-email"
          >
            <Icon icon="Refresh" />
          </Button>
        </Tooltip>
      ) : (
        ''
      )}
    </div>
  );
};

const MobileActions = ({
  review,
  resendEmail,
}: {
  review: OrderReview;
  resendEmail: (orderReviewId: number) => void;
}) => {
  return (
    <div className="w-full pl-2 pr-2 mt-2">
      {review.status === ReviewStatus.Pending ? (
        <Button
          className="button-secondary w-full mb-2"
          data-cy="resend-email"
          onClick={() => {
            resendEmail(review.order.id);
          }}
        >
          <Typography
            type="text-xs"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-grey-700"
          >
            Resend email
          </Typography>
        </Button>
      ) : (
        ''
      )}
    </div>
  );
};

const Legend = ({ index, color, label, size }: { index: number; color: string; label: string; size: number }) => {
  return (
    <div key={index} className="flex flex-row items-center space-x-2 mb-2">
      <Icon icon="Circle" size="small" stroke={`var(--${color})`} color={`var(--${color})`} />

      <Typography type="text-md" weight="regular" className="text-grey-400">
        {label}
      </Typography>
      <Typography type="text-md" weight="medium" className="mr-0.5">
        {size}
      </Typography>
    </div>
  );
};
